import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { FormTreeData, QuestionData } from '@/features/submission_forms/types';
import { SectionNode } from './SectionNode';
import { QuestionEditor } from './QuestionEditor';
import { SectionEditor } from './SectionEditor';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

export interface FormBuilderHandle {
  save: () => Promise<void>;
}

interface FormBuilderProps {
  initialSections: FormTreeData[];
  // Not needed when readOnly — nothing can change, so nothing is saved.
  onSave?: (sections: FormTreeData[]) => Promise<void>;
  // The parent (EditorPanel) drives the Save Changes / Publish buttons in
  // its own header — sections live here, so it needs a ref to trigger a
  // save, plus these to know whether one is needed or in flight.
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  // Browse the tree and each node's settings without being able to change
  // anything: no add/delete/drag, and the settings panel's inputs are
  // disabled. Used while templates are managed in code (DSL).
  readOnly?: boolean;
}

export const FormBuilder = forwardRef<FormBuilderHandle, FormBuilderProps>(
  (
    {
      initialSections,
      onSave,
      onDirtyChange,
      onSavingChange,
      readOnly = false,
    },
    ref
  ) => {
    const [sections, setSections] = useState<FormTreeData[]>(initialSections);
    const [selectedItem, setSelectedItem] = useState<{
      type: 'section' | 'question';
      id: string;
      path: number[];
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    // Snapshot of the last-saved (or initial) tree, to detect unsaved edits.
    const savedSectionsRef = useRef<FormTreeData[]>(initialSections);
    const isDirty = sections !== savedSectionsRef.current;

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
      onSavingChange?.(isSaving);
    }, [isSaving, onSavingChange]);

    // Find selected item in the tree
    const findItemInTree = useCallback(() => {
      if (!selectedItem) return null;

      const findInSections = (
        sectionsList: FormTreeData[],
        path: number[]
      ): FormTreeData | null => {
        if (path.length === 0) return null;
        const [first, ...rest] = path;
        const section = sectionsList[first];
        if (!section) return null;
        if (rest.length === 0) return section;
        return findInSections(section.children, rest);
      };

      if (selectedItem.type === 'section') {
        return findInSections(sections, selectedItem.path);
      } else {
        const section = findInSections(
          sections,
          selectedItem.path.slice(0, -1)
        );
        if (!section) return null;
        return section.questions[
          selectedItem.path[selectedItem.path.length - 1]
        ];
      }
    }, [selectedItem, sections]);

    // Update section in tree
    const updateSection = useCallback(
      (path: number[], updatedSection: Partial<FormTreeData>) => {
        const updateInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              if (rest.length === 0) {
                return { ...section, ...updatedSection };
              }
              return {
                ...section,
                children: updateInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(updateInSections(sections, path));
      },
      [sections]
    );

    // Update question in tree
    const updateQuestion = useCallback(
      (path: number[], updatedQuestion: Partial<QuestionData>) => {
        const sectionPath = path.slice(0, -1);
        const questionIdx = path[path.length - 1];

        const updateInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              if (rest.length === 0) {
                return {
                  ...section,
                  questions: section.questions.map((q, qIdx) =>
                    qIdx === questionIdx ? { ...q, ...updatedQuestion } : q
                  ),
                };
              }
              return {
                ...section,
                children: updateInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(updateInSections(sections, sectionPath));
      },
      [sections]
    );

    // Move section within same level
    const moveSection = useCallback(
      (fromPath: number[], toPath: number[]) => {
        // Simple implementation for same-level moves
        if (fromPath.length !== toPath.length) return;

        const parentPath = fromPath.slice(0, -1);
        const fromIdx = fromPath[fromPath.length - 1];
        const toIdx = toPath[toPath.length - 1];

        const updateInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) {
            const newList = [...sectionsList];
            const [moved] = newList.splice(fromIdx, 1);
            newList.splice(toIdx, 0, moved);
            return newList;
          }
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              return {
                ...section,
                children: updateInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(updateInSections(sections, parentPath));
      },
      [sections]
    );

    // Move question within same section
    const moveQuestion = useCallback(
      (sectionPath: number[], fromIdx: number, toIdx: number) => {
        const updateInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              if (rest.length === 0) {
                const newQuestions = [...section.questions];
                const [moved] = newQuestions.splice(fromIdx, 1);
                newQuestions.splice(toIdx, 0, moved);
                return { ...section, questions: newQuestions };
              }
              return {
                ...section,
                children: updateInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(updateInSections(sections, sectionPath));
      },
      [sections]
    );

    // Delete section
    const deleteSection = useCallback(
      (path: number[]) => {
        const deleteInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;

          if (rest.length === 0) {
            // Delete at this level
            return sectionsList.filter((_, idx) => idx !== first);
          }

          // Recurse deeper
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              return {
                ...section,
                children: deleteInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(deleteInSections(sections, path));
      },
      [sections]
    );

    // Delete question
    const deleteQuestion = useCallback(
      (path: number[]) => {
        const sectionPath = path.slice(0, -1);
        const questionIdx = path[path.length - 1];

        const deleteInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              if (rest.length === 0) {
                return {
                  ...section,
                  questions: section.questions.filter(
                    (_, qIdx) => qIdx !== questionIdx
                  ),
                };
              }
              return {
                ...section,
                children: deleteInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(deleteInSections(sections, sectionPath));
      },
      [sections]
    );

    // Add question to section
    const addQuestion = useCallback(
      (sectionPath: number[]) => {
        const newQuestion: QuestionData = {
          id: crypto.randomUUID(),
          slug: `question_${Date.now()}`,
          kind: 'text',
          prompt: 'New Question',
          required: false,
          depends_on: null,
          options: [],
          validations: {},
          meta: {},
          details: false,
          details_label: '',
        };

        const addInSections = (
          sectionsList: FormTreeData[],
          currentPath: number[]
        ): FormTreeData[] => {
          if (currentPath.length === 0) return sectionsList;
          const [first, ...rest] = currentPath;
          return sectionsList.map((section, idx) => {
            if (idx === first) {
              if (rest.length === 0) {
                return {
                  ...section,
                  questions: [...section.questions, newQuestion],
                };
              }
              return {
                ...section,
                children: addInSections(section.children, rest),
              };
            }
            return section;
          });
        };

        setSections(addInSections(sections, sectionPath));
      },
      [sections]
    );

    const handleSave = useCallback(async () => {
      if (readOnly || !onSave) return;
      setIsSaving(true);
      try {
        await onSave(sections);
        savedSectionsRef.current = sections;
        onDirtyChange?.(false);
      } finally {
        setIsSaving(false);
      }
    }, [readOnly, onSave, sections, onDirtyChange]);

    useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

    const handleSelectSection = useCallback((id: string, path: number[]) => {
      setSelectedItem({ type: 'section', id, path });
    }, []);

    const handleSelectQuestion = useCallback((id: string, path: number[]) => {
      setSelectedItem({ type: 'question', id, path });
    }, []);

    const selectedItemData = findItemInTree();

    return (
      <div className="flex h-full bg-gray-50">
        {/* Left Panel - Tree View */}
        <div className="flex-1 overflow-y-auto border-r border-gray-200 bg-white">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                Form Structure
              </h2>
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Add new root section
                    setSections([
                      ...sections,
                      {
                        id: crypto.randomUUID(),
                        label: 'New Section',
                        meta: {},
                        questions: [],
                        children: [],
                      },
                    ]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {sections.map((section, idx) => (
                <SectionNode
                  key={section.id}
                  section={section}
                  path={[idx]}
                  selectedId={selectedItem?.id}
                  onSelect={handleSelectSection}
                  onSelectQuestion={handleSelectQuestion}
                  onMoveSection={moveSection}
                  onMoveQuestion={moveQuestion}
                  onAddQuestion={addQuestion}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Editor */}
        <div className="w-96 overflow-y-auto bg-white shadow-lg">
          {selectedItem && selectedItemData ? (
            <div className="p-6">
              {selectedItem.type === 'section' ? (
                <SectionEditor
                  section={selectedItemData as FormTreeData}
                  onUpdate={(updated: Partial<FormTreeData>) =>
                    updateSection(selectedItem.path, updated)
                  }
                  onDelete={() => deleteSection(selectedItem.path)}
                  onClose={() => setSelectedItem(null)}
                  readOnly={readOnly}
                />
              ) : (
                <QuestionEditor
                  key={selectedItem.id}
                  question={selectedItemData as QuestionData}
                  allSections={sections}
                  onUpdate={(updated: Partial<QuestionData>) =>
                    updateQuestion(selectedItem.path, updated)
                  }
                  onDelete={() => deleteQuestion(selectedItem.path)}
                  onClose={() => setSelectedItem(null)}
                  readOnly={readOnly}
                />
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p className="mt-8">
                Select a section or question to {readOnly ? 'view' : 'edit'} its
                properties
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

FormBuilder.displayName = 'FormBuilder';
